/**
 * The conditions line on a flow rule row, rendered for the active scope.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Only METRIC conditions carry thresholds, so only they gain an
 * override/inherited badge. A ranking condition ("highest raw score") means the
 * same thing in every country and is deliberately left unbadged rather than
 * marked "inherited", which would imply a value it never had.
 */

import type { FC } from 'react';
import type { FlowTagCondition } from '@trafficpulse/shared';

import { formatScopedCondition } from '@/pages/admin/ListTagRulesPage/format-scoped-condition';
import { OverridesBadge } from '@/pages/admin/ListTagRulesPage/overrides';
import { isDefaultScope, type TagRuleScope, type ValueSource } from '@/features/tag-rule-scope';
import { cn } from '@/lib/utils';

import { toMetricCondition } from './flow-metric-condition';

const BADGE = 'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide';

const STYLE: Record<ValueSource, { label: string; cls: string; title: string }> = {
  own: {
    label: 'override',
    cls: 'bg-primary/12 text-primary',
    title: 'This scope has its own value for this metric',
  },
  country: {
    label: 'from country',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    title: 'No campaign value — this comes from the country override',
  },
  default: {
    label: 'inherited',
    cls: 'bg-muted text-muted-foreground',
    title: 'Nothing set for this scope — it follows the default',
  },
};

export const FlowScopedConditionLine: FC<{
  conditions: FlowTagCondition[];
  scope: TagRuleScope | undefined;
  formatBase: (c: FlowTagCondition) => string;
}> = ({ conditions, scope, formatBase }) => (
  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
    {conditions.map((c, i) => {
      const prefix = i > 0 ? <span className="text-muted-foreground/60">AND</span> : null;
      // Compare-mode conditions read like ranking ones: no scoped value, no
      // override badge — the threshold is the compared metric itself.
      if (c.kind !== 'metric' || c.compareTo) {
        return (
          <span key={i} className="inline-flex items-center gap-1.5">
            {prefix}
            <span>{formatBase(c)}</span>
          </span>
        );
      }
      const { text, state } = formatScopedCondition(
        toMetricCondition(c),
        scope,
        (plain) => formatBase({ ...c, ...plain, kind: 'metric' }),
      );
      const badge = state ? STYLE[state] : null;
      return (
        <span key={i} className="inline-flex items-center gap-1.5">
          {prefix}
          <span>{text}</span>
          {(!scope || isDefaultScope(scope)) && <OverridesBadge condition={c} />}
          {badge && (
            <span className={cn(BADGE, badge.cls)} title={badge.title}>
              {badge.label}
            </span>
          )}
        </span>
      );
    })}
  </p>
);
